import React, { useState, useRef, useEffect } from 'react'
import {
  DialogBase,
  FieldGrid,
  PrimaryCta,
  SecondaryCta,
  Input,
} from 'components'
import { Button, Typography } from '@mui/material'
import { useCreateBouserMutation, errorSnack } from 'features'
import { useDispatch } from 'react-redux'
import { BOUSER_MODEL_FIELDS } from 'constants'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useForm } from 'react-hook-form'

export default function CreateNewBOUserDialog(_props) {
  const {
    togglerComponent: Component = { Button },
    children,
    togglerProps = {},
  } = _props

  const [userCreated, setUserCreated] = useState(false)
  const [open, setOpen] = useState(false)

  const dispatch = useDispatch()

  function openDialog(e) {
    e.stopPropagation()
    setOpen(true)
  }
  function closeDialog() {
    setOpen(false)
    reset()
    setUserCreated(false)
  }

  const [createBouser, { isLoading }] = useCreateBouserMutation()

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      roleid: '',
    },
  })

  function onSubmit(data) {
    const mutation = createBouser({
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      roleid: data.roleid,
    }).unwrap()
    mutation
      .then((response) => {
        if (response?.message === 'invitation_sent') {
          setUserCreated(true)
        }
      })
      .catch((error) => {
        if (
          error.status === 400 ||
          error.data?.message === 'email_already_used'
        ) {
          dispatch(
            errorSnack({
              message: `Ya existe un usuario con el correo ${data.email}`,
            })
          )
          setError('email', {
            type: 'custom',
            message:
              'El correo ya está en uso, por favor introduzca otra dirección',
          })
        } else {
          dispatch(
            errorSnack({
              message: `Ocurrio un error procesando la solicitud, reintente mas tarde`,
            })
          )
        }
      })
  }

  const firstInputRef = useRef(null)

  useEffect(() => {
    /**
     * force focus on inoput field on first render
     */
    if (firstInputRef.current !== null) {
      firstInputRef.current.focus()
    }
  }, [])

  return (
    <>
      <Component onClick={openDialog} {...togglerProps}>
        {children}
      </Component>
      {open && (
        <DialogBase
          title={!userCreated ? 'Crear usuario' : 'Usuario creado'}
          open={open}
          handleClose={closeDialog}
        >
          {!userCreated ? (
            <>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col justify-start items-start gap-8 bg-white tests"
                autoComplete="off"
              >
                <FieldGrid columns={2}>
                  {[
                    BOUSER_MODEL_FIELDS.firstname,
                    BOUSER_MODEL_FIELDS.lastname,
                    BOUSER_MODEL_FIELDS.email,
                    BOUSER_MODEL_FIELDS.roleid,
                  ].map((field, index) => {
                    const {
                      name,
                      label = null,
                      validation = {},
                      ...rest
                    } = field
                    return (
                      <Input
                        ref={index === 0 ? firstInputRef : null}
                        autoFocus={index === 0}
                        key={name}
                        control={control}
                        name={name}
                        label={label || null}
                        defaultValue={''}
                        validation={validation || null}
                        errors={errors?.[name] || null}
                        type={field?.type || 'text'}
                        {...rest}
                      />
                    )
                  })}
                </FieldGrid>
                <div className="flex flex-row justify-center gap-4 w-full pt-">
                  <div className="w-1/2">
                    <SecondaryCta onClick={closeDialog} fullWidth>
                      Cancelar
                    </SecondaryCta>
                  </div>
                  <div className="w-1/2">
                    <PrimaryCta
                      type="submit"
                      fullWidth
                      isSubmitting={isLoading}
                      submittingLabel="Creando"
                    >
                      Crear
                    </PrimaryCta>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <>
              <Typography
                variant="h1"
                component="div"
                className="mb-8 text-success text-center opacity-50"
              >
                <FontAwesomeIcon icon={faCheckCircle} size="xl" />
              </Typography>
              <Typography variant="body1" className="mb-8 text-center">
                Se ha creado el usuario correctamente y enviado el email de
                invitación
              </Typography>
              <PrimaryCta onClick={closeDialog} fullWidth>
                Cerrar
              </PrimaryCta>
            </>
          )}
        </DialogBase>
      )}
    </>
  )
}
